/**
 * Subscription Plan Limits Configuration
 * Two-tier system: Free + Pro
 *
 * ============================================================
 * PRICING STRUCTURE
 * ============================================================
 *
 *   FREE        PRO
 *   $0          $2.49 (early bird — was $4.99)
 *
 * ============================================================
 * STORAGE MODEL
 * ============================================================
 * - Free → 200 MB LucidDock Cloud (Firestore)
 * - Pro  → Google Drive (user's own Drive, uses their quota)
 *
 * ============================================================
 * FREE PLAN ($0/forever)
 * Perfect for individuals getting started
 * ============================================================
 * - 1 user (Owner)
 * - Up to 5 addresses
 * - 5 share bundles
 * - AI Quick Links
 * - 200 MB Cloud storage
 *
 * ============================================================
 * PRO PLAN ($2.49/month — early bird)
 * Unlimited everything
 * ============================================================
 * Everything in Free, plus:
 * - Unlimited addresses
 * - Unlimited family members (Editor + Viewer)
 * - Unlimited sharing
 * - Unlimited photos & documents
 * - Google Drive storage (user's own quota)
 */

/**
 * Canonical plan type. 'premium' is a legacy alias for 'pro' —
 * some existing Firestore documents store 'premium' as the plan value.
 * Use normalizePlan() to convert before any logic.
 */
export type SubscriptionPlan = 'free' | 'pro' | 'premium';

/**
 * Normalize legacy 'premium' plan value to 'pro'.
 * Call this when reading plan from Firestore/localStorage.
 */
export function normalizePlan(plan: string | undefined): 'free' | 'pro' {
  if (plan === 'pro' || plan === 'premium') return 'pro';
  return 'free';
}

/**
 * Storage provider type — Free uses Cloud, Pro uses Google Drive
 */
export type PlanStorageProvider = 'cloud' | 'google-drive';

/**
 * Get the storage provider for a given plan
 */
export function getStorageProviderForPlan(plan: SubscriptionPlan): PlanStorageProvider {
  return usesGoogleDrive(plan) ? 'google-drive' : 'cloud';
}

/**
 * Check if plan uses Google Drive storage (Pro users can opt in)
 */
export function usesGoogleDrive(plan: SubscriptionPlan): boolean {
  return normalizePlan(plan) === 'pro';
}

/**
 * Check if plan is the paid Pro tier
 */
export function isPremiumPlan(plan: SubscriptionPlan): boolean {
  return normalizePlan(plan) === 'pro';
}

/**
 * Check if plan has AI features (all plans)
 */
export function hasAIFeatures(_plan: SubscriptionPlan): boolean {
  return true; // AI Quick Links available on all plans
}

/**
 * Check if plan is a paid plan
 */
export function isPaidPlan(plan: SubscriptionPlan): boolean {
  return normalizePlan(plan) === 'pro';
}

export interface PlanLimits {
  // Item count limits
  items: {
    maxAddresses: number;
    maxPersonal: number;
    maxVehicles: number;
    maxHealthcare: number;
    maxInsurance: number;
    maxFinancial: number;
  };
  // Address limits
  address: {
    maxAttachments: number; // Combined photos + docs per item
    maxContacts: number;
    maxUtilityAccounts: number;
    maxServiceProviders: number;
  };
  // Service provider limits
  serviceProvider: {
    maxAttachments: number;
  };
  // Vehicle limits
  vehicle: {
    maxAttachments: number;
    maxServiceCenters: number;
  };
  // Health limits
  health: {
    maxAttachments: number;
  };
  // Insurance limits
  insurance: {
    maxAttachments: number;
  };
  // Financial limits
  financial: {
    maxAttachments: number;
  };
  // Family limits
  family: {
    maxMembers: number;
    maxCoPilots: number;
    maxPassengers: number;
  };
  // Sharing limits
  sharing: {
    maxShareBundles: number; // Max share bundles a user can create
    affiliateLinks: boolean; // Whether affiliate/referral links can be included in bundles
  };
  // Global attachment limit (total across all entities)
  maxTotalAttachments: number;
  // Storage limits
  storage: {
    maxTotalMB: number;
    maxPhotoSizeMB: number;
    maxDocumentSizeMB: number;
    provider: PlanStorageProvider;
  };
}

// Use a large number to represent "unlimited" for paid plans
export const UNLIMITED = 999999;

// Legacy constant — kept for backward compat but no longer used
export const USER_QUOTA = -1;

/**
 * ============================================================
 * PRICING CONFIGURATION
 * ============================================================
 */

/** Monthly prices in USD — early bird: 50% off $4.99 */
export const PLAN_PRICES = {
  free: 0,
  pro: 2.49,      // early bird: 50% off $4.99
  premium: 2.49,   // legacy alias → same as pro
} as const;

/** Original (pre-discount) monthly prices — shown struck out on landing page */
export const PLAN_ORIGINAL_PRICES = {
  free: 0,
  pro: 4.99,
  premium: 4.99,   // legacy alias → same as pro
} as const;

/** Annual discount percentage */
export const ANNUAL_DISCOUNT_PERCENT = 20;

/**
 * Get the monthly price for a plan
 */
export function getPlanPrice(plan: SubscriptionPlan): number {
  return PLAN_PRICES[normalizePlan(plan)];
}

/**
 * Get annual price with discount
 */
export function getAnnualPrice(plan: SubscriptionPlan): number {
  const monthly = getPlanPrice(plan);
  const annualFull = monthly * 12;
  return Math.round(annualFull * (1 - ANNUAL_DISCOUNT_PERCENT / 100));
}

/**
 * Get monthly equivalent when paying annually
 */
export function getAnnualMonthlyPrice(plan: SubscriptionPlan): number {
  return Math.round((getAnnualPrice(plan) / 12) * 100) / 100;
}

/**
 * Format price for display (e.g. "$0", "$2.49")
 */
export function formatPrice(price: number): string {
  if (price === 0) return '$0';
  return `$${price.toFixed(2)}`;
}

/**
 * Get formatted display price string for a plan (e.g. "$2.49")
 */
export function getPlanPriceDisplay(plan: SubscriptionPlan): string {
  return formatPrice(getPlanPrice(plan));
}

/**
 * Get formatted annual price string for a plan (e.g. "$24")
 */
export function getAnnualPriceDisplay(plan: SubscriptionPlan): string {
  return `$${getAnnualPrice(plan)}`;
}

/**
 * Get original (pre-discount) price display for a plan (e.g. "$4.99")
 */
export function getOriginalPriceDisplay(plan: SubscriptionPlan): string | undefined {
  const n = normalizePlan(plan);
  const original = PLAN_ORIGINAL_PRICES[n] as number;
  const current = PLAN_PRICES[n] as number;
  if (!original || original === current) return undefined;
  return formatPrice(original);
}

/**
 * Get early bird savings percentage (e.g. 50)
 */
export function getEarlyBirdSavingsPercent(plan: SubscriptionPlan): number {
  const n = normalizePlan(plan);
  const original = PLAN_ORIGINAL_PRICES[n] as number;
  const current = PLAN_PRICES[n] as number;
  if (!original || original === current) return 0;
  return Math.round(((original - current) / original) * 100);
}

/**
 * Get plan price label with period (e.g. "$2.49/mo", "$0/forever")
 */
export function getPlanPriceLabel(plan: SubscriptionPlan): string {
  if (normalizePlan(plan) === 'free') return '$0/forever';
  return `${formatPrice(getPlanPrice(plan))}/mo`;
}

/**
 * System-wide maximum limits to prevent abuse
 * These caps apply regardless of subscription plan
 */
export const SYSTEM_MAX_UPLOADS = {
  ATTACHMENTS: 20,
} as const;

/**
 * Check if a limit value represents unlimited
 */
export function isUnlimited(limit: number): boolean {
  return limit >= UNLIMITED || limit === USER_QUOTA;
}

/**
 * Get the effective limit considering both plan limit and system max
 */
export function getEffectiveLimit(planLimit: number, systemMax: number): number {
  if (isUnlimited(planLimit)) return systemMax;
  return Math.min(planLimit, systemMax);
}

/**
 * Helper to format limit display - shows just count for unlimited/paid, count/max for free
 */
export function formatLimitDisplay(current: number, max: number, hideForUnlimited: boolean = false): string {
  if (isUnlimited(max) || hideForUnlimited) return `${current}`;
  return `${current}/${max}`;
}

/**
 * Check if limits should be hidden for the current plan
 */
export function shouldHideLimits(plan: SubscriptionPlan): boolean {
  return isPaidPlan(plan);
}

/**
 * Helper to get the max label
 */
export function formatMaxDisplay(current: number, max: number): string {
  if (isUnlimited(max)) return `${current} total`;
  return `${current}/${max} total`;
}

/**
 * Get display name for a plan
 */
export function getPlanDisplayName(plan: SubscriptionPlan): string {
  return normalizePlan(plan) === 'free' ? 'Free' : 'Pro';
}

/**
 * Get storage description for a plan
 */
export function getStorageDescription(plan: SubscriptionPlan): string {
  return normalizePlan(plan) === 'free' ? '200 MB Cloud' : 'Your Google Drive';
}

/**
 * Limits configuration per subscription plan
 */
export const PLAN_LIMITS: Record<SubscriptionPlan, PlanLimits> = {
  /**
   * Free Plan — $0/forever
   */
  free: {
    items: {
      maxAddresses: 5,
      maxPersonal: 10,
      maxVehicles: 5,
      maxHealthcare: 5,
      maxInsurance: 5,
      maxFinancial: 5,
    },
    address: {
      maxAttachments: 5,
      maxContacts: 5,
      maxUtilityAccounts: 5,
      maxServiceProviders: 5,
    },
    serviceProvider: { maxAttachments: 5 },
    vehicle: { maxAttachments: 5, maxServiceCenters: 5 },
    health: { maxAttachments: 5 },
    insurance: { maxAttachments: 5 },
    financial: { maxAttachments: 5 },
    family: {
      maxMembers: 1,
      maxCoPilots: 0,
      maxPassengers: 0,
    },
    maxTotalAttachments: 10,
    sharing: {
      maxShareBundles: 1,
      affiliateLinks: true,
    },
    storage: {
      maxTotalMB: 200,
      maxPhotoSizeMB: 5,
      maxDocumentSizeMB: 10,
      provider: 'cloud',
    },
  },

  /**
   * Pro Plan — $2.49/month (early bird)
   * Unlimited everything, Google Drive storage
   */
  pro: {
    items: {
      maxAddresses: UNLIMITED,
      maxPersonal: UNLIMITED,
      maxVehicles: UNLIMITED,
      maxHealthcare: UNLIMITED,
      maxInsurance: UNLIMITED,
      maxFinancial: UNLIMITED,
    },
    address: {
      maxAttachments: UNLIMITED,
      maxContacts: UNLIMITED,
      maxUtilityAccounts: UNLIMITED,
      maxServiceProviders: UNLIMITED,
    },
    serviceProvider: { maxAttachments: UNLIMITED },
    vehicle: { maxAttachments: UNLIMITED, maxServiceCenters: UNLIMITED },
    health: { maxAttachments: UNLIMITED },
    insurance: { maxAttachments: UNLIMITED },
    financial: { maxAttachments: UNLIMITED },
    family: {
      maxMembers: UNLIMITED,
      maxCoPilots: UNLIMITED,
      maxPassengers: UNLIMITED,
    },
    maxTotalAttachments: UNLIMITED,
    sharing: {
      maxShareBundles: UNLIMITED,
      affiliateLinks: true,
    },
    storage: {
      maxTotalMB: UNLIMITED,
      maxPhotoSizeMB: 50,
      maxDocumentSizeMB: 200,
      provider: 'google-drive',
    },
  },

  /** Legacy alias — identical to Pro. Existing Firestore docs may have 'premium'. */
  premium: {
    items: {
      maxAddresses: UNLIMITED,
      maxPersonal: UNLIMITED,
      maxVehicles: UNLIMITED,
      maxHealthcare: UNLIMITED,
      maxInsurance: UNLIMITED,
      maxFinancial: UNLIMITED,
    },
    address: {
      maxAttachments: UNLIMITED,
      maxContacts: UNLIMITED,
      maxUtilityAccounts: UNLIMITED,
      maxServiceProviders: UNLIMITED,
    },
    serviceProvider: { maxAttachments: UNLIMITED },
    vehicle: { maxAttachments: UNLIMITED, maxServiceCenters: UNLIMITED },
    health: { maxAttachments: UNLIMITED },
    insurance: { maxAttachments: UNLIMITED },
    financial: { maxAttachments: UNLIMITED },
    family: {
      maxMembers: UNLIMITED,
      maxCoPilots: UNLIMITED,
      maxPassengers: UNLIMITED,
    },
    maxTotalAttachments: UNLIMITED,
    sharing: {
      maxShareBundles: UNLIMITED,
      affiliateLinks: true,
    },
    storage: {
      maxTotalMB: UNLIMITED,
      maxPhotoSizeMB: 50,
      maxDocumentSizeMB: 200,
      provider: 'google-drive',
    },
  },
};

/**
 * ============================================================
 * PLAN TRANSITION RULES — Free ↔ Pro only
 * ============================================================
 */

const ALLOWED_TRANSITIONS = new Set<string>([
  'free→pro',
  'pro→free',
  // Legacy: existing users may have 'premium' stored
  'free→premium',
  'premium→free',
]);

export function isPlanTransitionAllowed(from: SubscriptionPlan, to: SubscriptionPlan): boolean {
  if (normalizePlan(from) === normalizePlan(to)) return false;
  return ALLOWED_TRANSITIONS.has(`${from}→${to}`);
}

export function getBlockedTransitionMessage(from: SubscriptionPlan, to: SubscriptionPlan): string {
  if (isPlanTransitionAllowed(from, to) || normalizePlan(from) === normalizePlan(to)) return '';
  return `To switch from ${getPlanDisplayName(from)} to ${getPlanDisplayName(to)}, please downgrade to Free first, then upgrade.`;
}

/** Default limits (free tier) — used as fallback */
export const DEFAULT_LIMITS = PLAN_LIMITS.free;

/** Get limits for a specific plan */
export function getLimitsForPlan(plan: SubscriptionPlan): PlanLimits {
  return PLAN_LIMITS[plan] || DEFAULT_LIMITS;
}
